"use strict";
var OrdersService_1;
var _a, _b, _c, _d, _e, _f, _g, _h;
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrdersService = void 0;
const tslib_1 = require("tslib");
const common_1 = require("@nestjs/common");
const typeorm_1 = require("@nestjs/typeorm");
const typeorm_2 = require("typeorm");
const order_entity_1 = require("./order.entity");
const order_line_item_entity_1 = require("./order-line-item.entity");
const inventory_stock_entity_1 = require("../inventory/inventory-stock.entity");
const customers_service_1 = require("../customers/customers.service");
const notifications_service_1 = require("../notifications/notifications.service");
const notification_log_entity_1 = require("../notifications/notification-log.entity");
const interaction_log_entity_1 = require("../customers/interaction-log.entity");
const shipment_entity_1 = require("./shipment.entity");
const shipment_line_item_entity_1 = require("./shipment-line-item.entity");
let OrdersService = OrdersService_1 = class OrdersService {
    constructor(orderRepo, lineRepo, shipmentRepo, shipmentLineRepo, stockRepo, customersService, notifService, dataSource) {
        this.orderRepo = orderRepo;
        this.lineRepo = lineRepo;
        this.shipmentRepo = shipmentRepo;
        this.shipmentLineRepo = shipmentLineRepo;
        this.stockRepo = stockRepo;
        this.customersService = customersService;
        this.notifService = notifService;
        this.dataSource = dataSource;
        this.logger = new common_1.Logger(OrdersService_1.name);
    }
    async findAll(customerId, status) {
        const where = {};
        if (customerId)
            where.customer = { id: customerId };
        if (status)
            where.status = status;
        return this.orderRepo.find({
            where,
            relations: [
                'customer',
                'customer.addresses',
                'shippingAddress',
                'lineItems',
                'lineItems.product',
                'lineItems.product.stockLocations',
                'lineItems.shipmentItems',
                'lineItems.shipmentItems.shipment',
                'shipments',
                'shipments.shipmentLineItems',
                'shipments.shipmentLineItems.lineItem',
            ],
            order: { createdAt: 'DESC' },
        });
    }
    async findById(id) {
        const order = await this.orderRepo.findOne({
            where: { id },
            relations: [
                'customer',
                'customer.addresses',
                'shippingAddress',
                'lineItems',
                'lineItems.product',
                'lineItems.product.stockLocations',
                'lineItems.shipmentItems',
                'lineItems.shipmentItems.shipment',
                'createdByAgent',
                'shipments',
                'shipments.shipmentLineItems',
                'shipments.shipmentLineItems.lineItem',
                'shipments.shipmentLineItems.lineItem.product',
            ],
        });
        if (!order)
            throw new common_1.NotFoundException(`Order #${id} not found`);
        return order;
    }
    async createShipment(dto) {
        const order = await this.findById(dto.orderId);
        const lineItemIds = Array.from(new Set((dto.lineItemIds ?? []).map((id) => Number(id)).filter(Boolean)));
        if (!lineItemIds.length) {
            throw new common_1.BadRequestException('Select at least one line item to create a shipment.');
        }
        const selectedLineItems = order.lineItems.filter((lineItem) => lineItemIds.includes(lineItem.id));
        if (selectedLineItems.length !== lineItemIds.length) {
            throw new common_1.BadRequestException('Some selected line items do not belong to this order.');
        }
        const assignedLineItems = selectedLineItems.filter((lineItem) => (lineItem.shipmentItems ?? []).length > 0);
        if (assignedLineItems.length) {
            throw new common_1.BadRequestException(`Line items already assigned to shipments: ${assignedLineItems.map((item) => item.id).join(', ')}`);
        }
        const warehouseLocation = dto.warehouseLocation ?? selectedLineItems[0].stockLocation ?? null;
        const destinationAddressId = dto.destinationAddressId ?? selectedLineItems[0].deliveryAddressId ?? order.shippingAddressId ?? null;
        const shippingMethod = selectedLineItems[0].shippingMethod;
        const bookedAt = new Date();
        const generatedAwbNumber = dto.awbNumber?.trim() || this.generateShipmentAwbNumber();
        const generatedTrackingNumber = dto.trackingNumber?.trim() || this.generateShipmentTrackingNumber(generatedAwbNumber);
        const missingFields = [];
        if (!`${dto.carrierCode ?? ''}`.trim())
            missingFields.push('carrierCode');
        if (!`${dto.carrierName ?? ''}`.trim())
            missingFields.push('carrierName');
        if (!`${dto.serviceCode ?? ''}`.trim())
            missingFields.push('serviceCode');
        if (!`${dto.serviceName ?? ''}`.trim())
            missingFields.push('serviceName');
        if (Number(dto.packageCount ?? 0) <= 0)
            missingFields.push('packageCount');
        if (Number(dto.totalWeightGrams ?? 0) <= 0)
            missingFields.push('totalWeightGrams');
        if (Number(dto.lengthCm ?? 0) <= 0)
            missingFields.push('lengthCm');
        if (Number(dto.widthCm ?? 0) <= 0)
            missingFields.push('widthCm');
        if (Number(dto.heightCm ?? 0) <= 0)
            missingFields.push('heightCm');
        if (!warehouseLocation) {
            throw new common_1.BadRequestException('Warehouse location is required to create a shipment.');
        }
        if (missingFields.length) {
            throw new common_1.BadRequestException(`Missing required shipment fields: ${missingFields.join(', ')}`);
        }
        for (const lineItem of selectedLineItems) {
            const lineDestination = lineItem.deliveryAddressId ?? order.shippingAddressId ?? null;
            if ((lineItem.stockLocation ?? null) !== warehouseLocation) {
                throw new common_1.BadRequestException('Create one shipment per warehouse location.');
            }
            if (lineDestination !== destinationAddressId) {
                throw new common_1.BadRequestException('Create one shipment per destination address.');
            }
            if (lineItem.shippingMethod !== shippingMethod) {
                throw new common_1.BadRequestException('Create one shipment per shipping method.');
            }
        }
        const shipmentId = await this.dataSource.transaction(async (mgr) => {
            const shipmentNumber = await this.generateShipmentNumber();
            const shipment = await mgr.save(mgr.create(shipment_entity_1.Shipment, {
                shipmentNumber,
                order: { id: order.id },
                orderId: order.id,
                warehouseLocation,
                destinationAddressId,
                carrierCode: dto.carrierCode ?? null,
                carrierName: dto.carrierName ?? null,
                serviceCode: dto.serviceCode ?? null,
                serviceName: dto.serviceName ?? null,
                awbNumber: generatedAwbNumber,
                trackingNumber: generatedTrackingNumber,
                packageCount: Math.max(1, Number(dto.packageCount ?? 1)),
                totalWeightGrams: Math.max(0, Number(dto.totalWeightGrams ?? 0)),
                lengthCm: dto.lengthCm ?? null,
                widthCm: dto.widthCm ?? null,
                heightCm: dto.heightCm ?? null,
                labelUrl: dto.labelUrl ?? null,
                manifestUrl: dto.manifestUrl ?? null,
                pickupRequestId: dto.pickupRequestId ?? null,
                bookingRequest: dto.bookingRequest ?? null,
                bookingResponse: dto.bookingResponse ?? null,
                notes: dto.notes ?? null,
                status: shipment_entity_1.ShipmentStatus.BOOKED,
                bookedAt,
            }));
            for (const lineItem of selectedLineItems) {
                await mgr.save(mgr.create(shipment_line_item_entity_1.ShipmentLineItem, {
                    shipment: { id: shipment.id },
                    shipmentId: shipment.id,
                    lineItem: { id: lineItem.id },
                    lineItemId: lineItem.id,
                    quantity: lineItem.quantity,
                }));
                await mgr.update(order_line_item_entity_1.OrderLineItem, lineItem.id, {
                    carrier: dto.carrierName ?? dto.carrierCode ?? null,
                    trackingId: generatedTrackingNumber,
                    status: lineItem.status,
                });
            }
            await mgr.update(order_entity_1.Order, order.id, {
                status: order_entity_1.OrderStatus.PROCESSING,
            });
            return shipment.id;
        });
        await this.recomputeOrderStatus(order.id);
        return this.findShipmentById(shipmentId);
    }
    async updateShipment(id, dto) {
        const shipment = await this.findShipmentById(id);
        const nextTracking = dto.trackingNumber ?? dto.awbNumber ?? shipment.trackingNumber ?? shipment.awbNumber ?? null;
        const nextCarrier = dto.carrierName ?? dto.carrierCode ?? shipment.carrierName ?? shipment.carrierCode ?? null;
        await this.shipmentRepo.update(id, {
            carrierCode: dto.carrierCode ?? shipment.carrierCode,
            carrierName: dto.carrierName ?? shipment.carrierName,
            serviceCode: dto.serviceCode ?? shipment.serviceCode,
            serviceName: dto.serviceName ?? shipment.serviceName,
            awbNumber: dto.awbNumber ?? shipment.awbNumber,
            trackingNumber: dto.trackingNumber ?? shipment.trackingNumber,
            packageCount: dto.packageCount ?? shipment.packageCount,
            totalWeightGrams: dto.totalWeightGrams ?? shipment.totalWeightGrams,
            lengthCm: dto.lengthCm === undefined ? shipment.lengthCm : dto.lengthCm,
            widthCm: dto.widthCm === undefined ? shipment.widthCm : dto.widthCm,
            heightCm: dto.heightCm === undefined ? shipment.heightCm : dto.heightCm,
            labelUrl: dto.labelUrl === undefined ? shipment.labelUrl : dto.labelUrl,
            manifestUrl: dto.manifestUrl === undefined ? shipment.manifestUrl : dto.manifestUrl,
            pickupRequestId: dto.pickupRequestId === undefined ? shipment.pickupRequestId : dto.pickupRequestId,
            notes: dto.notes === undefined ? shipment.notes : dto.notes,
            bookingRequest: dto.bookingRequest === undefined ? shipment.bookingRequest : dto.bookingRequest,
            bookingResponse: dto.bookingResponse === undefined ? shipment.bookingResponse : dto.bookingResponse,
            status: nextTracking && shipment.status === shipment_entity_1.ShipmentStatus.DRAFT ? shipment_entity_1.ShipmentStatus.BOOKED : shipment.status,
            bookedAt: nextTracking && !shipment.bookedAt ? new Date() : shipment.bookedAt,
        });
        await this.syncShipmentLineItems(id, {
            trackingId: nextTracking,
            carrier: nextCarrier,
            status: nextTracking ? order_line_item_entity_1.LineItemStatus.PACKED : undefined,
        });
        await this.recomputeOrderStatus(shipment.orderId);
        return this.findShipmentById(id);
    }
    async updateShipmentStatus(id, status, extra) {
        const shipment = await this.findShipmentById(id);
        const updates = {
            status,
            statusReason: extra?.statusReason ?? shipment.statusReason,
            latestWebhookPayload: extra?.latestWebhookPayload === undefined ? shipment.latestWebhookPayload : extra.latestWebhookPayload,
        };
        const trackingNumber = extra?.trackingNumber ?? extra?.awbNumber ?? shipment.trackingNumber ?? shipment.awbNumber ?? null;
        const carrierName = extra?.carrierName ?? extra?.carrierCode ?? shipment.carrierName ?? shipment.carrierCode ?? null;
        if (extra?.trackingNumber !== undefined)
            updates.trackingNumber = extra.trackingNumber;
        if (extra?.awbNumber !== undefined)
            updates.awbNumber = extra.awbNumber;
        if (extra?.carrierName !== undefined)
            updates.carrierName = extra.carrierName;
        if (extra?.carrierCode !== undefined)
            updates.carrierCode = extra.carrierCode;
        if (status === shipment_entity_1.ShipmentStatus.BOOKED && !shipment.bookedAt)
            updates.bookedAt = new Date();
        if (status === shipment_entity_1.ShipmentStatus.PACKED && !shipment.packedAt)
            updates.packedAt = new Date();
        if (status === shipment_entity_1.ShipmentStatus.DISPATCHED && !shipment.dispatchedAt)
            updates.dispatchedAt = new Date();
        if (status === shipment_entity_1.ShipmentStatus.DELIVERED && !shipment.deliveredAt)
            updates.deliveredAt = new Date();
        await this.shipmentRepo.update(id, updates);
        const lineStatus = this.mapShipmentStatusToLineStatus(status);
        if (lineStatus) {
            await this.syncShipmentLineItems(id, {
                trackingId: trackingNumber,
                carrier: carrierName,
                status: lineStatus,
                statusReason: extra?.statusReason,
            });
        }
        const refreshed = await this.findShipmentById(id);
        if (status === shipment_entity_1.ShipmentStatus.DISPATCHED) {
            await this.notifService.send({
                customerId: refreshed.order.customer.id,
                toPhone: refreshed.order.customer.phone,
                template: notification_log_entity_1.NotificationTemplate.ORDER_DISPATCHED,
                relatedEntityId: refreshed.orderId,
                relatedEntityType: 'order',
                variables: {
                    trackingId: trackingNumber ?? 'Pending',
                    carrier: carrierName ?? 'Courier',
                },
            });
        }
        if (status === shipment_entity_1.ShipmentStatus.DELIVERED) {
            await this.notifService.send({
                customerId: refreshed.order.customer.id,
                toPhone: refreshed.order.customer.phone,
                template: notification_log_entity_1.NotificationTemplate.ORDER_DELIVERED,
                relatedEntityId: refreshed.orderId,
                relatedEntityType: 'order',
            });
        }
        await this.recomputeOrderStatus(refreshed.orderId);
        return this.findShipmentById(id);
    }
    async findShipmentById(id) {
        const shipment = await this.shipmentRepo.findOne({
            where: { id },
            relations: [
                'order',
                'order.customer',
                'order.shippingAddress',
                'shipmentLineItems',
                'shipmentLineItems.lineItem',
                'shipmentLineItems.lineItem.product',
            ],
        });
        if (!shipment)
            throw new common_1.NotFoundException(`Shipment #${id} not found`);
        return shipment;
    }
    async create(dto) {
        try {
            const savedOrderId = await this.dataSource.transaction(async (mgr) => {
                const orderNumber = await this.generateOrderNumber();
                let subtotal = 0;
                let shippingTotal = 0;
                const order = mgr.create(order_entity_1.Order, {
                    orderNumber,
                    customer: { id: dto.customerId },
                    shippingAddress: dto.shippingAddressId ? { id: dto.shippingAddressId } : undefined,
                    createdByAgent: dto.agentId ? { id: dto.agentId } : undefined,
                    source: dto.source,
                    priority: dto.priority,
                    status: order_entity_1.OrderStatus.DRAFT,
                    notes: dto.notes,
                });
                const savedOrder = await mgr.save(order);
                for (const li of dto.lineItems) {
                    // Reserve inventory
                    const stock = await mgr.findOne(inventory_stock_entity_1.InventoryStock, {
                        where: { product: { id: li.productId }, location: li.stockLocation },
                        lock: { mode: 'pessimistic_write' },
                    });
                    if (!stock) {
                        throw new common_1.BadRequestException(`No stock record found for product #${li.productId} at location "${li.stockLocation}". Please re-seed stock data.`);
                    }
                    const available = stock.quantityOnHand - stock.quantityReserved;
                    if (available < li.quantity) {
                        throw new common_1.BadRequestException(`Only ${available} units available for product #${li.productId} at "${li.stockLocation}", requested ${li.quantity}.`);
                    }
                    await mgr.increment(inventory_stock_entity_1.InventoryStock, { id: stock.id }, 'quantityReserved', Number(li.quantity));
                    const lineTotal = li.unitPrice * li.quantity;
                    subtotal += lineTotal;
                    shippingTotal += li.shippingCharge;
                    await mgr.save(mgr.create(order_line_item_entity_1.OrderLineItem, {
                        order: savedOrder,
                        product: { id: li.productId },
                        quantity: li.quantity,
                        unitPrice: li.unitPrice,
                        lineTotal,
                        stockLocation: li.stockLocation,
                        deliveryAddressId: li.deliveryAddressId ?? undefined,
                        shippingMethod: li.shippingMethod,
                        shippingCharge: li.shippingCharge,
                        paymentMethod: li.paymentMethod,
                        isSplit: !!li.splitGroupId,
                        splitGroupId: li.splitGroupId,
                        status: order_line_item_entity_1.LineItemStatus.WROTE_SLIP,
                    }));
                }
                await mgr.update(order_entity_1.Order, savedOrder.id, {
                    subtotal,
                    shippingCharges: shippingTotal,
                    totalAmount: subtotal + shippingTotal,
                    status: dto.autoAllocateToLogistics ? order_entity_1.OrderStatus.ALLOCATED : order_entity_1.OrderStatus.CONFIRMED,
                    allocatedToLogisticsAt: dto.autoAllocateToLogistics ? new Date() : null,
                });
                // Return only the ID — fetching the full order inside the transaction
                // would use a separate connection that can't see uncommitted rows.
                return { id: savedOrder.id, orderNumber };
            });
            // ── Post-commit side-effects (outside transaction — no lock contention) ──
            // Promote to repeat patient if they have prior orders
            const orderCount = await this.orderRepo.count({ where: { customer: { id: dto.customerId } } });
            if (orderCount >= 1)
                await this.customersService.promoteToRepeat(dto.customerId);
            // Log interaction
            await this.customersService.addLog(dto.customerId, interaction_log_entity_1.LogType.ORDER_CREATED, `Order ${savedOrderId.orderNumber} created — ${dto.lineItems.length} line items`, dto.agentId, savedOrderId.id, 'order');
            // Transaction is committed now — safe to load the full order with relations.
            return this.findById(savedOrderId.id);
        }
        catch (err) {
            this.logger.error('Order creation failed', err instanceof Error ? err.stack : err);
            throw err;
        }
    }
    async createDraft(dto) {
        const orderNumber = await this.generateOrderNumber();
        const totals = this.calculateTotals(dto.lineItems);
        const order = await this.orderRepo.save(this.orderRepo.create({
            orderNumber,
            customer: { id: dto.customerId },
            shippingAddress: dto.shippingAddressId ? { id: dto.shippingAddressId } : undefined,
            createdByAgent: dto.agentId ? { id: dto.agentId } : undefined,
            source: dto.source,
            priority: dto.priority,
            status: order_entity_1.OrderStatus.DRAFT,
            notes: dto.notes,
            subtotal: totals.subtotal,
            shippingCharges: totals.shippingTotal,
            totalAmount: totals.totalAmount,
        }));
        await this.replaceDraftLineItems(order.id, dto.lineItems);
        return this.findById(order.id);
    }
    async updateDraft(id, dto) {
        const order = await this.findById(id);
        if (order.status !== order_entity_1.OrderStatus.DRAFT) {
            throw new common_1.BadRequestException('Only draft orders can be updated.');
        }
        const totals = this.calculateTotals(dto.lineItems);
        await this.orderRepo.update(id, {
            shippingAddressId: dto.shippingAddressId ?? null,
            source: dto.source,
            priority: dto.priority,
            notes: dto.notes,
            subtotal: totals.subtotal,
            shippingCharges: totals.shippingTotal,
            totalAmount: totals.totalAmount,
        });
        await this.replaceDraftLineItems(id, dto.lineItems);
        return this.findById(id);
    }
    async finalizeDraft(id, agentId) {
        const order = await this.findById(id);
        if (order.status !== order_entity_1.OrderStatus.DRAFT) {
            throw new common_1.BadRequestException('Only draft orders can be finalized.');
        }
        await this.dataSource.transaction(async (mgr) => {
            for (const li of order.lineItems) {
                const stock = await mgr.findOne(inventory_stock_entity_1.InventoryStock, {
                    where: { product: { id: li.productId }, location: li.stockLocation },
                    lock: { mode: 'pessimistic_write' },
                });
                if (!stock) {
                    throw new common_1.BadRequestException(`No stock record found for product #${li.productId} at location "${li.stockLocation}".`);
                }
                const available = stock.quantityOnHand - stock.quantityReserved;
                if (available < li.quantity) {
                    throw new common_1.BadRequestException(`Only ${available} units available for product #${li.productId} at "${li.stockLocation}", requested ${li.quantity}.`);
                }
                await mgr.increment(inventory_stock_entity_1.InventoryStock, { id: stock.id }, 'quantityReserved', Number(li.quantity));
                await mgr.update(order_line_item_entity_1.OrderLineItem, li.id, {
                    paymentStatus: order_line_item_entity_1.PaymentStatus.VERIFIED,
                    status: order_line_item_entity_1.LineItemStatus.WROTE_SLIP,
                });
            }
            await mgr.update(order_entity_1.Order, id, {
                status: order_entity_1.OrderStatus.ALLOCATED,
                allocatedToLogisticsAt: new Date(),
                createdByAgent: agentId ? { id: agentId } : order.createdByAgent,
                createdByAgentId: agentId ?? order.createdByAgentId,
            });
        });
        await this.customersService.addLog(order.customerId, interaction_log_entity_1.LogType.ORDER_CREATED, `Order ${order.orderNumber} finalized and sent to logistics`, agentId, order.id, 'order');
        return this.findById(id);
    }
    async removeDraft(id) {
        const order = await this.findById(id);
        if (order.status !== order_entity_1.OrderStatus.DRAFT) {
            throw new common_1.BadRequestException('Only draft orders can be removed.');
        }
        await this.orderRepo.delete(id);
    }
    async updateLineItemStatus(lineItemId, status, extra) {
        const updates = { status, ...extra };
        if (status === order_line_item_entity_1.LineItemStatus.DELIVERED) {
            updates.deliveredAt = new Date();
            const deadline = new Date();
            deadline.setHours(deadline.getHours() + 72);
            updates.issueReportDeadline = deadline;
        }
        if (status === order_line_item_entity_1.LineItemStatus.DISPATCHED && !updates.dispatchDate) {
            updates.dispatchDate = new Date();
        }
        // Release reservation on delivery / RTO / return
        if ([order_line_item_entity_1.LineItemStatus.DELIVERED, order_line_item_entity_1.LineItemStatus.RTO, order_line_item_entity_1.LineItemStatus.RETURNED].includes(status)) {
            const li = await this.lineRepo.findOne({ where: { id: lineItemId }, relations: ['product'] });
            if (li) {
                await this.stockRepo.decrement({ product: { id: li.product.id }, location: li.stockLocation }, 'quantityReserved', li.quantity);
                if (status !== order_line_item_entity_1.LineItemStatus.DELIVERED) {
                    // Return to stock on RTO / Return
                    await this.stockRepo.increment({ product: { id: li.product.id }, location: li.stockLocation }, 'quantityOnHand', li.quantity);
                }
            }
        }
        await this.lineRepo.update(lineItemId, updates);
        const updated = await this.lineRepo.findOneOrFail({ where: { id: lineItemId }, relations: ['shipmentItems', 'shipmentItems.shipment', 'order'] });
        if (updated.shipmentItems?.[0]?.shipmentId) {
            const mappedShipmentStatus = this.mapLineStatusToShipmentStatus(status);
            if (mappedShipmentStatus) {
                await this.shipmentRepo.update(updated.shipmentItems[0].shipmentId, {
                    status: mappedShipmentStatus,
                    trackingNumber: extra?.trackingId ?? updated.shipmentItems[0].shipment?.trackingNumber ?? null,
                    carrierName: extra?.carrier ?? updated.shipmentItems[0].shipment?.carrierName ?? null,
                    statusReason: extra?.statusReason ?? updated.shipmentItems[0].shipment?.statusReason ?? null,
                    dispatchedAt: status === order_line_item_entity_1.LineItemStatus.DISPATCHED ? (extra?.dispatchDate ?? new Date()) : updated.shipmentItems[0].shipment?.dispatchedAt,
                    deliveredAt: status === order_line_item_entity_1.LineItemStatus.DELIVERED ? new Date() : updated.shipmentItems[0].shipment?.deliveredAt,
                });
            }
            await this.recomputeOrderStatus(updated.orderId);
        }
        return this.lineRepo.findOneByOrFail({ id: lineItemId });
    }
    async markPaymentVerified(lineItemId) {
        await this.lineRepo.update(lineItemId, { paymentStatus: order_line_item_entity_1.PaymentStatus.VERIFIED });
    }
    async allocateToLogistics(orderId) {
        const order = await this.findById(orderId);
        await this.orderRepo.update(orderId, {
            status: order_entity_1.OrderStatus.ALLOCATED,
            allocatedToLogisticsAt: new Date(),
        });
        // Send WhatsApp to customer
        await this.notifService.send({
            customerId: order.customer.id,
            toPhone: order.customer.phone,
            template: notification_log_entity_1.NotificationTemplate.ORDER_WORKING,
            relatedEntityId: orderId,
            relatedEntityType: 'order',
        });
        return this.findById(orderId);
    }
    async syncShipmentLineItems(shipmentId, updates) {
        const shipmentItems = await this.shipmentLineRepo.find({
            where: { shipmentId },
            relations: ['lineItem'],
        });
        for (const shipmentItem of shipmentItems) {
            const patch = {};
            if (updates.trackingId !== undefined)
                patch.trackingId = updates.trackingId ?? null;
            if (updates.carrier !== undefined)
                patch.carrier = updates.carrier ?? null;
            if (updates.status !== undefined)
                patch.status = updates.status;
            if (updates.statusReason !== undefined)
                patch.statusReason = updates.statusReason ?? null;
            if (updates.status === order_line_item_entity_1.LineItemStatus.DISPATCHED)
                patch.dispatchDate = new Date();
            if (updates.status === order_line_item_entity_1.LineItemStatus.DELIVERED) {
                patch.deliveredAt = new Date();
                const deadline = new Date();
                deadline.setHours(deadline.getHours() + 72);
                patch.issueReportDeadline = deadline;
            }
            await this.lineRepo.update(shipmentItem.lineItemId, patch);
        }
    }
    async recomputeOrderStatus(orderId) {
        const order = await this.orderRepo.findOne({
            where: { id: orderId },
            relations: ['lineItems', 'shipments'],
        });
        if (!order)
            return;
        const lineStatuses = (order.lineItems ?? []).map((lineItem) => lineItem.status);
        let nextStatus = order.status;
        let completedAt = order.completedAt ?? null;
        if (lineStatuses.length > 0 && lineStatuses.every((status) => status === order_line_item_entity_1.LineItemStatus.DELIVERED)) {
            nextStatus = order_entity_1.OrderStatus.COMPLETED;
            completedAt = completedAt ?? new Date();
        }
        else if ((order.shipments ?? []).length > 0 || lineStatuses.some((status) => [order_line_item_entity_1.LineItemStatus.PACKED, order_line_item_entity_1.LineItemStatus.DISPATCHED, order_line_item_entity_1.LineItemStatus.IN_TRANSIT].includes(status))) {
            nextStatus = order_entity_1.OrderStatus.PROCESSING;
            completedAt = null;
        }
        else if (order.allocatedToLogisticsAt) {
            nextStatus = order_entity_1.OrderStatus.ALLOCATED;
            completedAt = null;
        }
        else if (order.status !== order_entity_1.OrderStatus.DRAFT) {
            nextStatus = order_entity_1.OrderStatus.CONFIRMED;
            completedAt = null;
        }
        if (nextStatus !== order.status || completedAt !== order.completedAt) {
            await this.orderRepo.update(orderId, {
                status: nextStatus,
                completedAt,
            });
        }
    }
    mapShipmentStatusToLineStatus(status) {
        const map = {
            [shipment_entity_1.ShipmentStatus.DRAFT]: null,
            [shipment_entity_1.ShipmentStatus.BOOKED]: order_line_item_entity_1.LineItemStatus.PACKED,
            [shipment_entity_1.ShipmentStatus.PACKED]: order_line_item_entity_1.LineItemStatus.PACKED,
            [shipment_entity_1.ShipmentStatus.DISPATCHED]: order_line_item_entity_1.LineItemStatus.DISPATCHED,
            [shipment_entity_1.ShipmentStatus.IN_TRANSIT]: order_line_item_entity_1.LineItemStatus.IN_TRANSIT,
            [shipment_entity_1.ShipmentStatus.DELIVERED]: order_line_item_entity_1.LineItemStatus.DELIVERED,
            [shipment_entity_1.ShipmentStatus.ON_HOLD]: order_line_item_entity_1.LineItemStatus.ON_HOLD,
            [shipment_entity_1.ShipmentStatus.RTO]: order_line_item_entity_1.LineItemStatus.RTO,
            [shipment_entity_1.ShipmentStatus.CANCELLED]: order_line_item_entity_1.LineItemStatus.ON_HOLD,
        };
        return map[status] ?? null;
    }
    mapLineStatusToShipmentStatus(status) {
        const map = {
            [order_line_item_entity_1.LineItemStatus.PACKED]: shipment_entity_1.ShipmentStatus.PACKED,
            [order_line_item_entity_1.LineItemStatus.DISPATCHED]: shipment_entity_1.ShipmentStatus.DISPATCHED,
            [order_line_item_entity_1.LineItemStatus.IN_TRANSIT]: shipment_entity_1.ShipmentStatus.IN_TRANSIT,
            [order_line_item_entity_1.LineItemStatus.DELIVERED]: shipment_entity_1.ShipmentStatus.DELIVERED,
            [order_line_item_entity_1.LineItemStatus.ON_HOLD]: shipment_entity_1.ShipmentStatus.ON_HOLD,
            [order_line_item_entity_1.LineItemStatus.RTO]: shipment_entity_1.ShipmentStatus.RTO,
            [order_line_item_entity_1.LineItemStatus.RETURNED]: shipment_entity_1.ShipmentStatus.RTO,
        };
        return map[status] ?? null;
    }
    async generateOrderNumber() {
        const year = new Date().getFullYear();
        const count = await this.orderRepo.count();
        return `ORD-${year}-${String(count + 1).padStart(5, '0')}`;
    }
    async generateShipmentNumber() {
        const year = new Date().getFullYear();
        const count = await this.shipmentRepo.count();
        return `SHP-${year}-${String(count + 1).padStart(5, '0')}`;
    }
    generateShipmentAwbNumber() {
        return `AWB${Date.now().toString().slice(-10)}`;
    }
    generateShipmentTrackingNumber(awbNumber) {
        return `TRK-${awbNumber}`;
    }
    calculateTotals(lineItems) {
        const subtotal = lineItems.reduce((sum, item) => sum + (Number(item.unitPrice) * Number(item.quantity)), 0);
        const shippingTotal = lineItems.reduce((sum, item) => sum + Number(item.shippingCharge ?? 0), 0);
        return {
            subtotal,
            shippingTotal,
            totalAmount: subtotal + shippingTotal,
        };
    }
    async replaceDraftLineItems(orderId, lineItems) {
        await this.lineRepo.delete({ orderId });
        const entities = lineItems.map((li) => this.lineRepo.create({
            order: { id: orderId },
            orderId,
            product: { id: li.productId },
            productId: li.productId,
            quantity: li.quantity,
            unitPrice: li.unitPrice,
            lineTotal: Number(li.unitPrice) * Number(li.quantity),
            stockLocation: li.stockLocation,
            deliveryAddressId: li.deliveryAddressId ?? null,
            shippingMethod: li.shippingMethod,
            shippingCharge: li.shippingCharge,
            paymentMethod: li.paymentMethod,
            paymentStatus: order_line_item_entity_1.PaymentStatus.PENDING,
            isSplit: !!li.isSplit || !!li.splitGroupId,
            splitGroupId: li.splitGroupId ?? null,
            status: order_line_item_entity_1.LineItemStatus.WROTE_SLIP,
        }));
        if (entities.length) {
            await this.lineRepo.save(entities);
        }
    }
};
exports.OrdersService = OrdersService;
exports.OrdersService = OrdersService = OrdersService_1 = tslib_1.__decorate([
    (0, common_1.Injectable)(),
    tslib_1.__param(0, (0, typeorm_1.InjectRepository)(order_entity_1.Order)),
    tslib_1.__param(1, (0, typeorm_1.InjectRepository)(order_line_item_entity_1.OrderLineItem)),
    tslib_1.__param(2, (0, typeorm_1.InjectRepository)(shipment_entity_1.Shipment)),
    tslib_1.__param(3, (0, typeorm_1.InjectRepository)(shipment_line_item_entity_1.ShipmentLineItem)),
    tslib_1.__param(4, (0, typeorm_1.InjectRepository)(inventory_stock_entity_1.InventoryStock)),
    tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _a : Object, typeof (_b = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _b : Object, typeof (_c = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _c : Object, typeof (_d = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _d : Object, typeof (_e = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _e : Object, typeof (_f = typeof customers_service_1.CustomersService !== "undefined" && customers_service_1.CustomersService) === "function" ? _f : Object, typeof (_g = typeof notifications_service_1.NotificationsService !== "undefined" && notifications_service_1.NotificationsService) === "function" ? _g : Object, typeof (_h = typeof typeorm_2.DataSource !== "undefined" && typeorm_2.DataSource) === "function" ? _h : Object])
], OrdersService);
